import { OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { PriceSuggestion } from '../../models/product-hire-estimate';
import { ConfigService } from '../services/config.service';
import { CurrencyPipe } from '@angular/common';
import { Subject } from 'rxjs';

@Pipe({
  name: 'priceEstimateBreakdown',
  standalone: true
})
export class PriceEstimateBreakdownPipe implements PipeTransform, OnDestroy {
  private unitsOrdered = ['year', 'month', 'week', 'day', 'hour', 'minute'];
  private ngUnsubscribe = new Subject<void>();

  constructor(
    private configService: ConfigService,
    private currencyPipe: CurrencyPipe
  ) {}

  transform(
    value: PriceSuggestion,
    currency = 'GBP',
    currencyRate = 1,
    itemQuantity = 1
  ): string {
    if (value && value.estimate !== null) {
      return value.estimate.descriptions
        .sort(
          (
            { price: { multiplier: aMultiplier, unit: aUnit } },
            { price: { multiplier: bMultiplier, unit: bUnit } }
          ) => {
            if (aUnit === bUnit) {
              return aMultiplier - bMultiplier;
            }
            return (
              this.unitsOrdered.indexOf(aUnit) -
              this.unitsOrdered.indexOf(bUnit)
            );
          }
        )
        .map((description) =>
          this.getDescriptionInCorrectCurrency(
            value.estimate,
            {
              code: currency,
              rate: currencyRate
            },
            description,
            itemQuantity
          )
        )
        .join(', \n')
        .trim();
    }
  }

  private getDescriptionInCorrectCurrency(
    estimate: PriceSuggestion['estimate'],
    overRiddenCurrency: {
      code: string;
      rate: number;
    },
    description: {
      quantity: number;
      price: {
        type: string;
        label: string;
        value: number;
        unit: string;
        multiplier: number;
      };
    },
    itemQuantity: number
  ): string {
    // if the estimate is in the same currency as the overridden currency, use the default currency rate
    if (estimate.currency_code === overRiddenCurrency.code) {
      return this.getDescriptionString(
        description,
        1,
        overRiddenCurrency.code,
        itemQuantity
      );
    }
    // if the estimate is in a different currency, use the overridden currency rate (assumes the rate is in the default currency of the HL account)
    return this.getDescriptionString(
      description,
      overRiddenCurrency.rate,
      overRiddenCurrency.code,
      itemQuantity
    );
  }

  private getDescriptionString(
    description: {
      quantity: number;
      price: {
        type: string;
        label: string;
        value: number;
        unit: string;
        multiplier: number;
      };
    },
    currencyRate: number,
    currency: string,
    itemQuantity = 1
  ): string {
    let prefix = '';
    if (itemQuantity > 1) {
      prefix = `${itemQuantity} x `;
    }
    return `${prefix}${description.quantity} x ${
      description.price.unit
    } @ ${this.currencyPipe.transform(
      (description.price.value * currencyRate) / 100,
      currency
    )}`;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
